import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import ChatPageV2 from '../Chat/ChatPageV2';
import BulkSender from '../Bulk/BulkSenderPage';
import HelpPage from '../../pages/Help';
import ConnectionErrorBoundary from '../Common/ConnectionErrorBoundary';
import ChatErrorBoundary from '../Common/ChatErrorBoundary';
import RateLimitIndicator from '../Common/RateLimitIndicator';
import { useErrorRecovery } from '../../hooks/useErrorRecovery';
import { useHeartbeat } from '../../hooks/useHeartbeat';
import { useChatState, useProgressState, useConnectionState } from '../../store/useAppStore';

interface ConnectedStateProps {
  authId: string;
  onLogout: () => void;
}


export const ConnectedState: React.FC<ConnectedStateProps> = ({ authId, onLogout }) => {
  const { isOnline } = useErrorRecovery();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // State hooks
  const { isConnected } = useConnectionState();
  const { allChats, refreshChats } = useChatState();
  const hasTriedAutoLoad = useRef(false);

  // Initialize heartbeat monitoring
  const {
    isHeartbeatRunning,
    consecutiveFailures,
    lastSuccessfulHeartbeat
  } = useHeartbeat({
    interval: 30000, // 30 seconds
    timeout: 8000, // 8 second timeout
    maxFailures: 3, // 3 consecutive failures
    enabled: true
  });

  // Auto-load groups when connected for the first time (with rate limit awareness and retry logic)
  useEffect(() => {
    if (isConnected && authId && !hasTriedAutoLoad.current && allChats.length === 0) {
      hasTriedAutoLoad.current = true;
      console.log('🚀 Auto-loading groups on first connection...');
      
      // Initial delay to allow connection to stabilize
      const autoLoadTimer = setTimeout(async () => {
        console.log('📡 Attempting auto-load of groups...');
        
        try {
          // Don't force load to respect rate limits
          await refreshChats(false);
          
          // Check if groups loaded successfully after a short delay
          setTimeout(() => {
            if (allChats.length === 0) {
              console.log('⚠️ Auto-load didn\'t return groups, user may need to manually refresh');
            } else {
              console.log(`✅ Auto-load successful: ${allChats.length} groups loaded`);
            }
          }, 2000);
          
        } catch (error) {
          console.error('❌ Auto-load failed:', error);
          // Don't show error toast for auto-load failures
        }
      }, 3000); // Reduced to 3 seconds for better UX
      
      return () => clearTimeout(autoLoadTimer);
    }
  }, [isConnected, authId, allChats.length, refreshChats]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--wa-gray-50)' }}>
      {/* Modern Header */}
      <nav 
        className="sticky top-0 wa-gradient-navbar-yellow"
        style={{ 
          zIndex: 'var(--wa-z-sticky)'
        }}
        role="navigation"
        aria-label="Navegação principal"
      >
        <div className="mx-auto" style={{ maxWidth: '80rem', padding: '0 var(--wa-space-6)' }}>
          <div className="flex justify-between items-center" style={{ height: '72px', minHeight: '72px' }}>
            
            {/* Logo */}
            <div className="flex items-center" style={{ gap: 'var(--wa-space-3)' }}>
              <div 
                className="wa-gradient-yellow-primary flex items-center justify-center wa-hover-scale"
                style={{ 
                  width: 'var(--wa-space-12)', 
                  height: 'var(--wa-space-12)', 
                  borderRadius: 'var(--wa-rounded-xl)',
                  border: '2px solid var(--wa-gold-400)',
                  cursor: 'pointer'
                }}
              >
                <span style={{ color: 'var(--wa-yellow-900)', fontSize: 'var(--wa-text-xl)', fontWeight: 'var(--wa-font-bold)' }}>📱</span>
              </div>
              <div>
                <h1 className="wa-heading-4" style={{ color: 'var(--wa-yellow-900)', fontWeight: 'var(--wa-font-bold)' }}>
                  Whats Automation
                </h1>
                <p className="wa-caption" style={{ color: 'var(--wa-yellow-700)', fontWeight: 'var(--wa-font-medium)' }}>v2.0</p>
              </div>
            </div>
            
            {/* Navigation */}
            <nav className="hidden md:flex items-center" style={{ gap: 'var(--wa-space-3)' }}>
              <NavLink 
                to="/" 
                className={({ isActive }) => `wa-body-small wa-focus-ring wa-nav-item-yellow ${isActive ? 'active' : ''}`}
                style={({ isActive }) => ({
                  padding: 'var(--wa-space-3) var(--wa-space-5)',
                  fontWeight: 'var(--wa-font-medium)',
                  textDecoration: 'none',
                  color: isActive ? 'var(--wa-yellow-900)' : 'var(--wa-yellow-800)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--wa-space-2)'
                })}
              >
                <span style={{ fontSize: 'var(--wa-text-lg)' }}>💬</span>
                <span>Chats</span>
              </NavLink>
              
              <div className="wa-nav-separator-yellow"></div>
              
              <NavLink 
                to="/bulk" 
                className={({ isActive }) => `wa-body-small wa-focus-ring wa-nav-item-yellow ${isActive ? 'active' : ''}`}
                style={({ isActive }) => ({
                  padding: 'var(--wa-space-3) var(--wa-space-5)',
                  fontWeight: 'var(--wa-font-medium)',
                  textDecoration: 'none',
                  color: isActive ? 'var(--wa-yellow-900)' : 'var(--wa-yellow-800)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--wa-space-2)'
                })}
              >
                <span style={{ fontSize: 'var(--wa-text-lg)' }}>📤</span>
                <span>Envio em Massa</span>
              </NavLink>
              
              <div className="wa-nav-separator-yellow"></div>
              
              <NavLink 
                to="/ajuda" 
                className={({ isActive }) => `wa-body-small wa-focus-ring wa-nav-item-yellow ${isActive ? 'active' : ''}`}
                style={({ isActive }) => ({
                  padding: 'var(--wa-space-3) var(--wa-space-5)',
                  fontWeight: 'var(--wa-font-medium)',
                  textDecoration: 'none',
                  color: isActive ? 'var(--wa-yellow-900)' : 'var(--wa-yellow-800)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--wa-space-2)'
                })}
              >
                <span style={{ fontSize: 'var(--wa-text-lg)' }}>❓</span>
                <span>Ajuda</span>
              </NavLink>
            </nav>
            
            {/* Status & Actions */}
            <div className="flex items-center" style={{ gap: 'var(--wa-space-5)' }}>
              
              {/* Connection Status */}
              <div className="hidden sm:flex items-center" style={{ gap: 'var(--wa-space-2)' }}>
                <div 
                  className="flex items-center wa-status-yellow"
                  style={{ padding: 'var(--wa-space-2) var(--wa-space-4)', borderRadius: 'var(--wa-rounded-full)' }}
                >
                  <div 
                    className="wa-animate-pulse"
                    style={{ 
                      width: 'var(--wa-space-2)', 
                      height: 'var(--wa-space-2)', 
                      backgroundColor: isOnline ? 'var(--wa-success-500)' : 'var(--wa-error-500)',
                      borderRadius: 'var(--wa-rounded-full)',
                      marginRight: 'var(--wa-space-2)'
                    }}
                  ></div>
                  <span style={{ fontSize: 'var(--wa-text-sm)', fontWeight: 'var(--wa-font-bold)' }}>
                    {isOnline ? '🟢 Conectado' : '🔴 Offline'}
                  </span>
                </div>
              </div>

              <div className="wa-nav-separator-yellow"></div>
              
              {/* User Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="wa-focus-ring flex items-center wa-hover-scale"
                  style={{
                    padding: 'var(--wa-space-3) var(--wa-space-4)',
                    borderRadius: 'var(--wa-rounded-full)',
                    border: '2px solid var(--wa-gold-300)',
                    background: 'rgba(255, 248, 225, 0.9)',
                    backdropFilter: 'blur(15px)',
                    cursor: 'pointer',
                    transition: 'var(--wa-transition-hover)',
                    gap: 'var(--wa-space-3)',
                    boxShadow: '0 4px 15px rgba(255, 193, 7, 0.2)'
                  }}
                  aria-label="Menu do usuário"
                  aria-expanded={isDropdownOpen}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 235, 59, 0.9)';
                    e.currentTarget.style.borderColor = 'var(--wa-gold-400)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 193, 7, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 248, 225, 0.9)';
                    e.currentTarget.style.borderColor = 'var(--wa-gold-300)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 193, 7, 0.2)';
                  }}
                >
                  <div 
                    className="wa-gradient-yellow-primary flex items-center justify-center"
                    style={{ 
                      width: '36px', 
                      height: '36px', 
                      borderRadius: 'var(--wa-rounded-full)',
                      border: '2px solid var(--wa-gold-500)'
                    }}
                  >
                    <span style={{ fontSize: 'var(--wa-text-base)', color: 'var(--wa-yellow-900)' }}>👤</span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <div style={{ fontSize: 'var(--wa-text-sm)', fontWeight: 'var(--wa-font-bold)', color: 'var(--wa-yellow-900)' }}>
                      WhatsApp
                    </div>
                    <div style={{ fontSize: 'var(--wa-text-xs)', color: 'var(--wa-yellow-700)', fontWeight: 'var(--wa-font-medium)' }}>
                      {authId ? `${authId.substring(0, 8)}...` : 'Não conectado'}
                    </div>
                  </div>
                  <div 
                    style={{ 
                      fontSize: 'var(--wa-text-sm)', 
                      color: 'var(--wa-yellow-700)',
                      transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'var(--wa-transition-normal)',
                      fontWeight: 'var(--wa-font-bold)'
                    }}
                  >
                    ▼
                  </div>
                </button>

                {/* Dropdown Menu */}
                <div className={`wa-dropdown-menu-yellow ${isDropdownOpen ? 'open' : ''}`}>
                  <div style={{ padding: 'var(--wa-space-5)' }}>
                    {/* User Info */}
                    <div style={{ marginBottom: 'var(--wa-space-4)', paddingBottom: 'var(--wa-space-3)', borderBottom: `2px solid rgba(255, 193, 7, 0.3)` }}>
                      <div style={{ fontSize: 'var(--wa-text-base)', fontWeight: 'var(--wa-font-bold)', color: 'var(--wa-yellow-900)' }}>
                        🔐 Sessão WhatsApp
                      </div>
                      <div style={{ fontSize: 'var(--wa-text-sm)', color: 'var(--wa-yellow-800)', marginTop: 'var(--wa-space-2)', fontWeight: 'var(--wa-font-medium)' }}>
                        ID: {authId ? `${authId.substring(0, 12)}...` : 'Não conectado'}
                      </div>
                      <div style={{ fontSize: 'var(--wa-text-sm)', color: isOnline ? 'var(--wa-success-700)' : 'var(--wa-error-700)', marginTop: 'var(--wa-space-1)', fontWeight: 'var(--wa-font-bold)' }}>
                        Status: {isOnline ? '🟢 Online' : '🔴 Offline'}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--wa-space-3)' }}>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          if (confirm('Tem certeza que deseja desconectar do WhatsApp?')) {
                            onLogout();
                          }
                        }}
                        className="flex items-center wa-focus-ring wa-hover-scale"
                        style={{
                          width: '100%',
                          padding: 'var(--wa-space-3) var(--wa-space-4)',
                          borderRadius: 'var(--wa-rounded-lg)',
                          border: '2px solid var(--wa-error-300)',
                          background: 'linear-gradient(135deg, var(--wa-error-50), var(--wa-warning-50))',
                          color: 'var(--wa-error-800)',
                          cursor: 'pointer',
                          transition: 'var(--wa-transition-hover)',
                          gap: 'var(--wa-space-3)',
                          fontSize: 'var(--wa-text-sm)',
                          fontWeight: 'var(--wa-font-bold)',
                          boxShadow: '0 2px 8px rgba(239, 68, 68, 0.1)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, var(--wa-error-100), var(--wa-warning-100))';
                          e.currentTarget.style.borderColor = 'var(--wa-error-400)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(239, 68, 68, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, var(--wa-error-50), var(--wa-warning-50))';
                          e.currentTarget.style.borderColor = 'var(--wa-error-300)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.1)';
                        }}
                      >
                        <span style={{ fontSize: 'var(--wa-text-lg)' }}>🚪</span>
                        <span>Desconectar</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div 
          className="md:hidden wa-gradient-navbar-yellow"
          style={{ 
            borderTop: `2px solid rgba(255, 193, 7, 0.4)`
          }}
        >
          <div className="flex justify-around" style={{ padding: 'var(--wa-space-4) var(--wa-space-2)' }}>
            <NavLink 
              to="/" 
              className={({ isActive }) => `flex flex-col items-center wa-caption wa-focus-ring ${isActive ? 'active' : ''}`}
              style={({ isActive }) => ({
                padding: 'var(--wa-space-3)',
                textDecoration: 'none',
                borderRadius: 'var(--wa-rounded-xl)',
                transition: 'var(--wa-transition-hover)',
                background: isActive ? 'rgba(255, 235, 59, 0.3)' : 'rgba(255, 248, 225, 0.5)',
                border: isActive ? '2px solid var(--wa-gold-400)' : '2px solid transparent',
                color: isActive ? 'var(--wa-yellow-900)' : 'var(--wa-yellow-800)',
                minWidth: '80px',
                boxShadow: isActive ? '0 4px 15px rgba(255, 193, 7, 0.2)' : 'none'
              })}
            >
              <span style={{ fontSize: 'var(--wa-text-2xl)', marginBottom: 'var(--wa-space-1)' }}>💬</span>
              <span style={{ fontSize: 'var(--wa-text-xs)', fontWeight: 'var(--wa-font-bold)' }}>Chats</span>
            </NavLink>
            
            <NavLink 
              to="/bulk" 
              className={({ isActive }) => `flex flex-col items-center wa-caption wa-focus-ring ${isActive ? 'active' : ''}`}
              style={({ isActive }) => ({
                padding: 'var(--wa-space-3)',
                textDecoration: 'none',
                borderRadius: 'var(--wa-rounded-xl)',
                transition: 'var(--wa-transition-hover)',
                background: isActive ? 'rgba(255, 235, 59, 0.3)' : 'rgba(255, 248, 225, 0.5)',
                border: isActive ? '2px solid var(--wa-gold-400)' : '2px solid transparent',
                color: isActive ? 'var(--wa-yellow-900)' : 'var(--wa-yellow-800)',
                minWidth: '80px',
                boxShadow: isActive ? '0 4px 15px rgba(255, 193, 7, 0.2)' : 'none'
              })}
            >
              <span style={{ fontSize: 'var(--wa-text-2xl)', marginBottom: 'var(--wa-space-1)' }}>📤</span>
              <span style={{ fontSize: 'var(--wa-text-xs)', fontWeight: 'var(--wa-font-bold)' }}>Envio</span>
            </NavLink>
            
            <NavLink 
              to="/ajuda" 
              className={({ isActive }) => `flex flex-col items-center wa-caption wa-focus-ring ${isActive ? 'active' : ''}`}
              style={({ isActive }) => ({
                padding: 'var(--wa-space-3)',
                textDecoration: 'none',
                borderRadius: 'var(--wa-rounded-xl)',
                transition: 'var(--wa-transition-hover)',
                background: isActive ? 'rgba(255, 235, 59, 0.3)' : 'rgba(255, 248, 225, 0.5)',
                border: isActive ? '2px solid var(--wa-gold-400)' : '2px solid transparent',
                color: isActive ? 'var(--wa-yellow-900)' : 'var(--wa-yellow-800)',
                minWidth: '80px',
                boxShadow: isActive ? '0 4px 15px rgba(255, 193, 7, 0.2)' : 'none'
              })}
            >
              <span style={{ fontSize: 'var(--wa-text-2xl)', marginBottom: 'var(--wa-space-1)' }}>❓</span>
              <span style={{ fontSize: 'var(--wa-text-xs)', fontWeight: 'var(--wa-font-bold)' }}>Ajuda</span>
            </NavLink>

            {/* Mobile User Menu */}
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex flex-col items-center wa-caption wa-focus-ring wa-hover-scale"
              style={{
                padding: 'var(--wa-space-3)',
                border: '2px solid var(--wa-gold-300)',
                background: 'rgba(255, 248, 225, 0.8)',
                borderRadius: 'var(--wa-rounded-xl)',
                transition: 'var(--wa-transition-hover)',
                color: 'var(--wa-yellow-800)',
                minWidth: '80px',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(255, 193, 7, 0.15)'
              }}
              aria-label="Menu do usuário"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 235, 59, 0.8)';
                e.currentTarget.style.borderColor = 'var(--wa-gold-400)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 193, 7, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 248, 225, 0.8)';
                e.currentTarget.style.borderColor = 'var(--wa-gold-300)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 193, 7, 0.15)';
              }}
            >
              <span style={{ fontSize: 'var(--wa-text-2xl)', marginBottom: 'var(--wa-space-1)' }}>👤</span>
              <span style={{ fontSize: 'var(--wa-text-xs)', fontWeight: 'var(--wa-font-bold)' }}>Perfil</span>
            </button>
          </div>
        </div>
      </nav>
      
      {/* Single Column Layout - No Sidebar */}
      <main style={{ 
        minHeight: 'calc(100vh - 72px)',
        maxWidth: '80rem',
        margin: '0 auto',
        padding: 'var(--wa-space-6)'
      }}>
        {/* Rate Limit Indicator */}
        <RateLimitIndicator className="mb-4" />
        
          <Routes>
            <Route 
              path="/" 
              element={
                <ConnectionErrorBoundary>
                  <ChatErrorBoundary>
                    <ChatPageV2 />
                  </ChatErrorBoundary>
                </ConnectionErrorBoundary>
              } 
            />
            <Route 
              path="/bulk" 
              element={
                <ConnectionErrorBoundary>
                  <BulkSender authId={authId} />
                </ConnectionErrorBoundary>
              } 
            />
            <Route path="/ajuda" element={<HelpPage />} />
        </Routes>
      </main>
    </div>
  );
};